/**
 * What the attachment quick view (PdfPreviewDialog) can show — always
 * read-only; editing happens in the suite apps (Docs / Sheets / Slides):
 *   - pdf    — the PDF viewer, as is (it asks for the password of a protected PDF)
 *   - word   — Word as a read-only page (the Docs service's HTML export)
 *   - sheet  — Excel as an Excel-like grid (the Sheet service's /Preview cells)
 *   - office — PowerPoint, converted by the Slides service's /ToPdf
 *   - image  — shown directly
 *   - text   — plain text as a page; CSV / TSV as a table
 *   - zip    — the files inside, each previewable; asks for the password of a
 *              protected zip (read in the browser — the password never leaves it)
 * Anything else downloads instead.
 */
export type PreviewKind = "pdf" | "word" | "sheet" | "office" | "image" | "text" | "zip";

const WORD = new Set(["doc", "docx", "rtf"]);
const SHEET = new Set(["xls", "xlsx", "xlsm"]);
const OFFICE = new Set(["ppt", "pptx"]);
const IMAGE = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif", "ico"]);
const TEXT = new Set(["txt", "csv", "tsv", "log", "md", "json", "xml"]);

export function extensionOf(fileName: string): string {
    return fileName.includes(".") ? fileName.split(".").pop()!.toLowerCase() : "";
}

export function previewKind(fileName: string, contentType = ""): PreviewKind | null {
    const ext = extensionOf(fileName);
    const type = contentType.toLowerCase();
    if (ext === "pdf" || type === "application/pdf") return "pdf";
    if (ext === "zip" || type === "application/zip" || type === "application/x-zip-compressed") return "zip";
    if (WORD.has(ext)) return "word";
    if (SHEET.has(ext)) return "sheet";
    if (OFFICE.has(ext)) return "office";
    if (IMAGE.has(ext) || (type.startsWith("image/") && type !== "image/tiff")) return "image";
    if (TEXT.has(ext) || type === "text/plain" || type === "text/csv") return "text";
    return null;
}

export function canQuickView(fileName: string, contentType?: string): boolean {
    return previewKind(fileName, contentType) !== null;
}
